import { Routes, Route, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import ChoosePseudo from './pages/ChoosePseudo';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import DataDeletionStatus from './pages/DataDeletionStatus';
import MagicLinkRequest from './pages/MagicLinkRequest';
import MagicLinkCallback from './pages/MagicLinkCallback';
import AccountOnboarding from './pages/AccountOnboarding';
import MainLayout from './components/MainLayout';
import { fetchGameHashPass } from './lib/sessionCredentials';
import { decodeJwtPayload } from './lib/jwt';
import type { Session } from './types/app';

/**
 * Enrichit une session avec les claims utiles décodés depuis le JWT
 * (notamment `licence_valid` pour l'accès Espace Compétition).
 * Les flux OAuth / magic link / select-pseudo ne fournissent pas toujours
 * cette info dans leur payload JSON ; le JWT lui la contient toujours.
 *
 * @param applyDefaults Si true, applique aussi les défauts de session
 *   (activer le mode `compet` si le joueur est éligible). À utiliser
 *   uniquement lors d'une connexion fraîche, pas lors de la restauration
 *   depuis le localStorage (sinon le toggle « sortir du mode compétition »
 *   serait réinitialisé à chaque refresh).
 */
function enrichSessionFromToken(session: Session, applyDefaults = false): Session {
  const payload = decodeJwtPayload(session.token);
  if (!payload) return session;
  const licence = payload.licence_valid === true;
  const next: Session = { ...session, hasCompetitionAccess: licence };
  if (applyDefaults && licence && !session.compet) {
    next.compet = true;
  }
  return next;
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const savedSession = localStorage.getItem('wt_neo_session');
    if (savedSession) {
      try {
        setSession(enrichSessionFromToken(JSON.parse(savedSession)));
      } catch {
        localStorage.removeItem('wt_neo_session');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (session) {
      localStorage.setItem('wt_neo_session', JSON.stringify(session));
    } else {
      localStorage.removeItem('wt_neo_session');
    }
  }, [session]);

  // Récupérer / synchroniser le hashPass legacy pour le serveur Java (OAuth inclus)
  useEffect(() => {
    if (!session?.token) return;

    let cancelled = false;
    fetchGameHashPass(session.token).then((hashPass) => {
      if (!cancelled && hashPass) {
        setSession((prev) => (prev && prev.hashPass !== hashPass ? { ...prev, hashPass } : prev));
      }
    });

    return () => { cancelled = true; };
  }, [session?.token]);

  const handleLogin = useCallback((newSession: Session) => {
    setSession(enrichSessionFromToken(newSession, true));
    // Nettoyer l'URL OAuth (/auth/callback?token=..., /auth/choose-pseudo?session=...)
    navigate('/', { replace: true });
  }, [navigate]);

  const handleLogout = useCallback(() => {
    navigate('/', { replace: true });
    setSession(null);
  }, [navigate]);

  const handleToggleCompet = () => {
    if (!session) return;
    setSession({ ...session, compet: !session.compet });
  };

  const handleOnboardingDone = useCallback(() => {
    setSession((prev) => (prev ? { ...prev, isNewAccount: false } : prev));
    navigate('/', { replace: true });
  }, [navigate]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Chargement...</p>
      </div>
    );
  }

  if (session) {
    if (session.isNewAccount) {
      return (
        <AccountOnboarding
          session={session}
          onCompleted={handleOnboardingDone}
        />
      );
    }
    return (
      <MainLayout
        session={session}
        onLogout={handleLogout}
        onToggleCompet={handleToggleCompet}
        onSessionUpdate={setSession}
      />
    );
  }

  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
      <Route path="/auth/choose-pseudo" element={<ChoosePseudo onLogin={handleLogin} />} />
      <Route path="/register" element={<Register onLogin={handleLogin} />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/magic-link" element={<MagicLinkRequest />} />
      <Route path="/auth/magic" element={<MagicLinkCallback onLogin={handleLogin} />} />
      <Route path="/confidentialite" element={<PrivacyPolicy />} />
      <Route path="/cgu" element={<TermsOfService />} />
      <Route path="/suppression-donnees" element={<DataDeletionStatus />} />
      <Route path="*" element={<Login onLogin={handleLogin} />} />
    </Routes>
  );
}

export default App;
